/**
 * useSidebarReveal — 좌측 사이드바 등장 시 [data-sidebar-item] 스태거 reveal
 *
 * 사용 예시:
 *   const ref = useSidebarReveal<HTMLElement>();
 *   return (
 *     <nav ref={ref}>
 *       <a data-sidebar-item href="/">Dashboard</a>
 *       <a data-sidebar-item href="/lab">Lab</a>
 *     </nav>
 *   );
 */
import { useRef } from 'react';
import type { RefObject } from 'react';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { MOTION_TOKENS } from '../tokens';
import { useReducedMotion } from './useReducedMotion';

const SIDEBAR_ITEM_SELECTOR = '[data-sidebar-item]' as const;
const SLIDE_DISTANCE = -16; // px (좌측에서 진입)

export function useSidebarReveal<T extends HTMLElement = HTMLElement>(
  /** 사이드바가 마운트된 뒤 reveal을 트리거할 ready 플래그 */
  ready = true,
): RefObject<T | null> {
  const containerRef = useRef<T | null>(null);
  const reducedMotion = useReducedMotion();

  useGSAP(() => {
    const container = containerRef.current;
    if (!container || !ready) return;

    const items = container.querySelectorAll(SIDEBAR_ITEM_SELECTOR);
    if (items.length === 0) return;

    if (reducedMotion) {
      gsap.set(items, { opacity: 1, x: 0 });
      return;
    }

    gsap.fromTo(
      items,
      { opacity: 0, x: SLIDE_DISTANCE },
      {
        opacity: 1,
        x: 0,
        stagger: MOTION_TOKENS.stagger.tight,
        duration: MOTION_TOKENS.duration.generous,
        ease: MOTION_TOKENS.ease.default,
        clearProps: 'transform,opacity',
      },
    );
  }, { scope: containerRef, dependencies: [ready, reducedMotion] });

  return containerRef;
}
